import React, { useReducer, useState, useMemo } from 'react';
import { useForm, useWatch, useFormState } from 'react-hook-form';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';

import { capture, adjustAuth } from 'redux/slices/transaction';
import visa_logo from 'assets/icons/visa_blue.svg';

import Tooltip from 'components/Tooltip';
import PriceInput from 'components/PriceInput';
import ModalWithChildren from 'components/ModalWithChildren';
import { capturePayment as TEXT } from 'locales/en/transaction';
import { priceToDisplay, priceForAPI, priceFromAPI } from 'utils/price';
import { CURRENCY_SYMBOL } from 'utils/transform';
import {
  FEE_COMMISSION,
  FEE_MARKETPLACE,
  FEE_BUSINESS_CARD,
  FEE_COST_ESTIMATION,
  FEE_TXTPAY_JAVELIN,
  FEE_TXTPAY_COMPANY,
} from './helpers';
import FeeStructureInput from './FeeStructureInput';
import schema from './CapturePayment.schema';

const CapturePayment = ({ transaction, className, onClick }) => {
  const INITIAL_AMOUNT = priceFromAPI(
    transaction.amount -
      transaction.txtPayCompanyFee -
      transaction.txtPayJavelinFee
  );

  const [fees, setFees] = useReducer((state, nextState) => {
    return {
      ...state,
      ...nextState,
    };
  }, {});

  const { control, handleSubmit } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      amount: INITIAL_AMOUNT,
    },
  });
  const { errors } = useFormState({ control });

  const _amount = useWatch({
    control,
    name: 'amount',
    defaultValue: INITIAL_AMOUNT,
  });

  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const onClickConfirm = ({ amount }) => {
    setIsLoading(true);
    if (needsAuthAdjustment) {
      dispatch(adjustAuth({ id: transaction.id, amount })).then(() => {
        setIsOpen(false);
        setIsLoading(false);
      });
    } else {
      dispatch(capture({ id: transaction.id, amount, transactionType: transaction.integration_type })).then(() => {
        setIsOpen(false);
        setIsLoading(false);
      });
    }
  };

  const onClickCancel = () => {
    setIsOpen(false);
  };

  const feeStructures = useMemo(() => {
    const isApplicable = (feeStructure) => {
      if ([FEE_COMMISSION, FEE_COST_ESTIMATION].includes(feeStructure.layout)) {
        return false;
      }

      if (
        !transaction.isBusinessCard &&
        feeStructure.layout === FEE_BUSINESS_CARD
      ) {
        return false;
      }

      if (
        [FEE_TXTPAY_JAVELIN, FEE_TXTPAY_COMPANY].includes(
          feeStructure.layout
        ) &&
        !transaction.shouldChargeTxtpayFees
      ) {
        return false;
      }

      return true;
    };

    return (
      transaction.feeStructure?.structure?.filter((_structure) =>
        isApplicable(_structure)
      ) || []
    );
  }, [
    transaction.feeStructure,
    transaction.isBusinessCard,
    transaction.shouldChargeTxtpayFees,
  ]);

  const shouldAutoCalc = (feeStructure) => {
    return [FEE_BUSINESS_CARD, FEE_MARKETPLACE].includes(feeStructure.layout);
  };

  const isStatic = (feeStructure) => {
    return [FEE_TXTPAY_JAVELIN, FEE_TXTPAY_COMPANY].includes(
      feeStructure.layout
    );
  };
  const needsAuthAdjustment =
    !transaction.supports_overcapture &&
    priceForAPI(_amount) > transaction.amount;

  const isUserFacing = (feeStructure) => {
    return [FEE_TXTPAY_JAVELIN, FEE_TXTPAY_COMPANY, FEE_BUSINESS_CARD].includes(
      feeStructure.layout
    );
  };

  const totalAmount = useMemo(() => {
    let TOTAL = _amount;

    for (let feeStructure of feeStructures) {
      if (isUserFacing(feeStructure)) {
        TOTAL += fees[feeStructure.key] || 0;
      }
    }

    return TOTAL;
  }, [_amount, feeStructures, fees]);

  return (
    <>
      <button
        className={className}
        onClick={() => {
          if (onClick) {
            onClick();
          }
          setIsOpen(true);
        }}
      >
        {TEXT.title}
      </button>
      {isOpen && (
        <ModalWithChildren
          isOpen
          header={TEXT.header}
          isLoading={isLoading}
          ctaTitle={isLoading ? TEXT.capturing : TEXT.title}
          onClickCta={handleSubmit(onClickConfirm)}
          cta2Title={TEXT.cancel}
          onClickCta2={onClickCancel}
        >
          <>
            <div className="mt-12">
              <div className="flex flex-row items-center">
                <Tooltip
                  text={`User facing fees are added to the order price. Net amount may not be equal to the order price after applying the fees.`}
                  className="relative"
                />
                <span className="ml-2 text-sm text-javelin-gray-dark inline-block">
                  {TEXT.message('€', priceToDisplay(totalAmount))}
                  <img
                    className="w-6 mx-1 overflow-visible inline rounded-sm"
                    src={visa_logo}
                    alt={`visa ending in ${transaction.last4}`}
                  />
                  <span className="h-full">ending in {transaction.last4}</span>
                </span>
              </div>
            </div>
            <div className="grid gap-y-2 mt-7">
              <PriceInput
                label={
                  <span className="text-xs text-javelin-gray-light w-40">
                    {TEXT.amount}
                  </span>
                }
                className="flex flex-col lg:items-baseline mb-0 lg:flex-row"
                inputClassName="flex-1"
                name="amount"
                control={control}
                inputPrefix={CURRENCY_SYMBOL[transaction.currency]}
                thousandSeparator
                fixedDecimalScale
                decimalScale={2}
                error={errors?.amount?.message}
              />
              {!!feeStructures.length &&
                feeStructures.map((feeStructure) => {
                  return (
                    <FeeStructureInput
                      key={feeStructure.key}
                      feeStructure={feeStructure}
                      fees={fees}
                      transaction={transaction}
                      transactionAmount={priceForAPI(_amount)}
                      shouldAutoCalc={shouldAutoCalc(feeStructure)}
                      isStatic={isStatic(feeStructure)}
                      setFees={setFees}
                    />
                  );
                })}
              <span
                className={`text-xs text-javelin-gray-light h-0 ${
                  needsAuthAdjustment ? 'visible' : 'invisible'
                }`}
              >
                {TEXT.requireAdjustment}
              </span>
            </div>
          </>
        </ModalWithChildren>
      )}
    </>
  );
};

CapturePayment.propTypes = {
  transaction: PropTypes.shape({}),
  className: PropTypes.string,
};
CapturePayment.defaultProps = {
  className: 'btn btn-secondary w-40 h-11 px-0',
};

export default CapturePayment;
